import { StoreAnalytics } from "./analytics-client";
import AnalyticsService from "../services/analytics-service";

export class StorePageTracker extends StoreAnalytics {
  static async trackPageView(): Promise<void> {
    try {
      const user = await this.getCurrentUser();
      const userInfo = this.getUserInfo(user);

      const data = {
        sessionId: this.getOrCreateSessionId(),
        path: window.location.pathname,
        title: document.title,
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
        storeSection: this.getCurrentStoreSection(),
        deviceType: this.getDeviceType(),
        utm: this.getUtmParameters(),
        ...userInfo,
      };

      await AnalyticsService.trackPageView(data);
    } catch (error) {
      console.error("Failed to track page view:", error);
    }
  }
}

export class StoreEventTracker extends StoreAnalytics {
  static async trackStoreEvent(
    eventType: string,
    eventData: Record<string, any> = {}
  ): Promise<void> {
    try {
      const user = await this.getCurrentUser();
      const userInfo = this.getUserInfo(user);

      const data = {
        sessionId: this.getOrCreateSessionId(),
        eventType,
        eventCategory: "store_interaction",
        eventAction: eventData.action || "view",
        eventLabel: eventData.label,
        eventValue: eventData.value,
        pageUrl: window.location.href,
        timestamp: new Date().toISOString(),
        ...userInfo,
      };

      await AnalyticsService.trackEvent(data);
    } catch (error) {
      console.error("Failed to track store event:", error);
    }
  }

  // Convenience methods for common events
  static async trackProductView(
    productId: string,
    category?: string,
    price?: number
  ): Promise<void> {
    return this.trackStoreEvent("product_view", {
      action: "view",
      label: `Product ${productId}`,
      value: price?.toString(),
      productId,
      category,
    });
  }

  static async trackAddToCart(
    productId: string,
    quantity: number,
    price: number
  ): Promise<void> {
    return this.trackStoreEvent("add_to_cart", {
      action: "add",
      label: `Product ${productId}`,
      value: price.toString(),
      productId,
      quantity,
    });
  }

  static async trackRemoveFromCart(
    productId: string,
    quantity: number,
    price: number
  ): Promise<void> {
    return this.trackStoreEvent("remove_from_cart", {
      action: "remove",
      label: `Product ${productId}`,
      value: price.toString(),
      productId,
      quantity,
    });
  }

  static async trackCheckoutStep(
    step: string,
    cartValue: number,
    itemCount?: number
  ): Promise<void> {
    return this.trackStoreEvent("checkout_step", {
      action: "step",
      label: step,
      value: cartValue.toString(),
      itemCount,
    });
  }

  static async trackPurchase(
    orderId: string,
    totalValue: number,
    itemCount: number,
    paymentMethod?: string
  ): Promise<void> {
    return this.trackStoreEvent("purchase", {
      action: "complete",
      label: `Order ${orderId}`,
      value: totalValue.toString(),
      itemCount,
      paymentMethod,
    });
  }

  static async trackSearch(
    query: string,
    resultsCount?: number
  ): Promise<void> {
    return this.trackStoreEvent("search", {
      action: "search",
      label: query,
      value: resultsCount?.toString(),
      query,
    });
  }

  static async trackUserRegistration(method: string = "email"): Promise<void> {
    return this.trackStoreEvent("user_registration", {
      action: "register",
      label: method,
      method,
    });
  }

  static async trackUserLogin(method: string = "email"): Promise<void> {
    return this.trackStoreEvent("user_login", {
      action: "login",
      label: method,
      method,
    });
  }
}
